import { useMemo } from 'react';
import { lineAt, lyricLinesOf, LyricSyncState } from 'fym-shared/src/music24/lyricTiming';
import { useGetLyricTimingQuery } from '../../../../../redux/api/fymAPI';
import { FrameLyric, frameLyricOf } from './assetFrameVars';

interface UseFrameLyricProps {
  accountId: number;
  itemUuid: string;
  song: { title: string; lyrics: string | null; hookStartSec: number | null; lyricSyncState?: LyricSyncState };
}

/**
 * The sung line of the drawn frames (design s7: "the synced line at the hook"): the synced line at
 * the hook of the analysis, else the second line of the lyrics. Undefined for a song with no
 * lyrics: the frames then show the title.
 */
export function useFrameLyric({ accountId, itemUuid, song }: UseFrameLyricProps): FrameLyric | undefined {
  const isSynced = song.lyricSyncState === 'synced';
  const { data } = useGetLyricTimingQuery({ accountId, itemUuid }, { skip: !isSynced });
  const timing = data?.timing ?? null;

  return useMemo(() => {
    const texts = lyricLinesOf(song.lyrics).map((line) => line.text);
    if (texts.length === 0) {
      return undefined;
    }
    const atHook = timing !== null && song.hookStartSec !== null ? lineAt(timing, song.hookStartSec + 1) : null;
    const index = atHook === null ? Math.min(1, texts.length - 1) : atHook.index;
    return frameLyricOf(texts, index, song.title);
  }, [song.hookStartSec, song.lyrics, song.title, timing]);
}
